import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Users, Search, Filter, MoreVertical, Ban, CheckCircle, Mail, UserCog } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlatformAdminUsers = () => {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState({ coaches: [], parents: [], athletes: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('coaches');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/platform-admin/users`, { headers: getAuthHeaders() });
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [getAuthHeaders]);

  const handleSuspend = async (userType, userId) => {
    if (!window.confirm('Are you sure you want to suspend this user?')) return;
    try {
      await axios.post(`${API}/platform-admin/user/${userType}/${userId}/suspend`, {}, { headers: getAuthHeaders() });
      toast.success('User suspended');
      fetchData();
    } catch (error) {
      toast.error('Failed to suspend user');
    }
  };

  const handleActivate = async (userType, userId) => {
    try {
      await axios.post(`${API}/platform-admin/user/${userType}/${userId}/activate`, {}, { headers: getAuthHeaders() });
      toast.success('User activated');
      fetchData();
    } catch (error) {
      toast.error('Failed to activate user');
    }
  };

  const tabs = [
    { id: 'coaches', label: 'Coaches', count: data.coaches?.length || 0 },
    { id: 'parents', label: 'Parents', count: data.parents?.length || 0 },
    { id: 'athletes', label: 'Athletes', count: data.athletes?.length || 0 },
  ];

  const filterItems = (items) => {
    if (!searchQuery) return items;
    const query = searchQuery.toLowerCase();
    return items.filter(item => 
      item.name?.toLowerCase().includes(query) ||
      item.full_name?.toLowerCase().includes(query) ||
      item.email?.toLowerCase().includes(query)
    );
  };

  const renderCoaches = () => {
    const coaches = filterItems(data.coaches || []);
    return (
      <div className="space-y-2">
        {coaches.map((coach) => (
          <div key={coach.id} className="bg-card border border-border/40 p-4 rounded-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                  <span className="text-blue-400 font-bold">{coach.full_name?.[0] || 'C'}</span>
                </div>
                <div>
                  <p className="font-medium">{coach.full_name}</p>
                  <p className="text-sm text-muted-foreground">{coach.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <span className={`px-2 py-1 rounded text-xs uppercase ${
                    coach.subscription?.status === 'active' 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    {coach.subscription?.plan || 'No Plan'}
                  </span>
                  <p className="text-xs text-muted-foreground mt-1">
                    {coach.athlete_count || 0} athletes
                  </p>
                </div>
                <div className="flex gap-1">
                  {coach.status === 'suspended' ? (
                    <Button size="sm" variant="ghost" onClick={() => handleActivate('coach', coach.id)}>
                      <CheckCircle size={16} className="text-green-400" />
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => handleSuspend('coach', coach.id)}>
                      <Ban size={16} className="text-red-400" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
            {coach.status === 'suspended' && (
              <div className="mt-2 px-3 py-1 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-400">
                Account Suspended
              </div>
            )}
          </div>
        ))}
        {coaches.length === 0 && (
          <p className="text-center text-muted-foreground py-8">No coaches found</p>
        )}
      </div>
    );
  };

  const renderParents = () => {
    const parents = filterItems(data.parents || []);
    return (
      <div className="space-y-2">
        {parents.map((parent) => (
          <div key={parent.id} className="bg-card border border-border/40 p-4 rounded-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                  <span className="text-purple-400 font-bold">{parent.full_name?.[0] || 'P'}</span>
                </div>
                <div>
                  <p className="font-medium">{parent.full_name}</p>
                  <p className="text-sm text-muted-foreground">{parent.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className={`px-2 py-1 rounded text-xs uppercase ${
                  parent.signup_type === 'independent' 
                    ? 'bg-amber-500/20 text-amber-400' 
                    : 'bg-blue-500/20 text-blue-400'
                }`}>
                  {parent.signup_type === 'independent' ? 'Independent' : 'Via Coach'}
                </span>
                <div className="flex gap-1">
                  {parent.status === 'suspended' ? (
                    <Button size="sm" variant="ghost" onClick={() => handleActivate('parent', parent.id)}>
                      <CheckCircle size={16} className="text-green-400" />
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => handleSuspend('parent', parent.id)}>
                      <Ban size={16} className="text-red-400" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
        {parents.length === 0 && (
          <p className="text-center text-muted-foreground py-8">No parents found</p>
        )}
      </div>
    );
  };

  const renderAthletes = () => {
    const athletes = filterItems(data.athletes || []);
    return (
      <div className="space-y-2">
        {athletes.map((athlete) => (
          <div key={athlete.id} className="bg-card border border-border/40 p-4 rounded-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                  <span className="text-green-400 font-bold">{athlete.name?.[0] || 'A'}</span>
                </div>
                <div>
                  <p className="font-medium">{athlete.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {athlete.age} years • {athlete.sport} • {athlete.position || 'No position'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <span className={`px-2 py-1 rounded text-xs uppercase ${
                    athlete.coach_id ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'
                  }`}>
                    {athlete.coach_id ? 'Has Coach' : 'No Coach'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
        {athletes.length === 0 && (
          <p className="text-center text-muted-foreground py-8">No athletes found</p>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl uppercase tracking-tight">User Management</h1>
          <p className="text-muted-foreground">Manage coaches, parents, and athletes</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm uppercase tracking-wider rounded-t transition-colors ${
              activeTab === tab.id
                ? 'bg-primary/10 text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
        <Input
          placeholder="Search by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Content */}
      {activeTab === 'coaches' && renderCoaches()}
      {activeTab === 'parents' && renderParents()}
      {activeTab === 'athletes' && renderAthletes()}
    </div>
  );
};

export default PlatformAdminUsers;
